/**
 * Карта v3 — раскладка.
 *
 * Две беды прошлых версий, которые здесь закрываются по построению:
 *   • контейнеры локаций налезали друг на друга — здесь они укладываются
 *     полками с гарантированным зазором и не пересекаются никогда;
 *   • устройства вылезали за свою локацию — здесь размер контейнера считается
 *     снизу вверх от содержимого, а при ручном перетаскивании работает
 *     удержание внутри рамки.
 */
import { GAP, PAD, PAD_TOP, NODE_W, NODE_H } from './const';
import { childrenOf, innerBox } from './scene';
import { rectsOverlap, mtv, clamp, unionRect } from './math';

/** Сколько столбцов дать n устройствам, чтобы блок был близок к золотому прямоугольнику. */
function gridCols(n) {
  if (n <= 1) return 1;
  return Math.max(1, Math.round(Math.sqrt(n * 1.45)));
}

/**
 * Упаковка полками (first-fit decreasing height): дети складываются рядами,
 * ряд переносится, когда не влезает по ширине. Простая, предсказуемая и,
 * в отличие от силовой раскладки, даёт один и тот же результат каждый раз.
 */
function packShelves(items, maxWidth, gap) {
  const shelves = [];
  let row = [], rowW = 0, rowH = 0;
  for (const it of items) {
    if (row.length && rowW + gap + it.w > maxWidth) {
      shelves.push({ items: row, w: rowW, h: rowH });
      row = []; rowW = 0; rowH = 0;
    }
    if (row.length) rowW += gap;
    row.push(it);
    rowW += it.w;
    rowH = Math.max(rowH, it.h);
  }
  if (row.length) shelves.push({ items: row, w: rowW, h: rowH });
  return shelves;
}

/**
 * Рекурсивно считает размер локации по её содержимому и раскладывает детей
 * относительно левого верхнего угла. Возвращает требуемые w/h.
 */
function sizeAndPlace(s, locId, seen = new Set()) {
  if (seen.has(locId)) return { w: 240, h: 160 };   // защита от циклов в дереве
  seen.add(locId);

  const c = s.conts.get(locId);
  if (!c) return { w: 240, h: 160 };

  const { nodes, conts } = childrenOf(s, locId);

  // сначала посчитать размеры вложенных локаций
  const childBoxes = [];
  for (const cc of conts) {
    const sz = sizeAndPlace(s, cc.id, seen);
    cc.w = sz.w; cc.h = sz.h;
    childBoxes.push({ ref: cc, w: cc.w, h: cc.h, isCont: true });
  }
  for (const n of nodes) {
    childBoxes.push({ ref: n, w: n.w, h: n.h, isCont: false });
  }

  if (!childBoxes.length) {
    c.w = Math.max(c.w || 0, 260);
    c.h = Math.max(c.h || 0, 150);
    return { w: c.w, h: c.h };
  }

  // вложенные локации идут первыми и целыми рядами — так читается иерархия
  childBoxes.sort((a, b) => {
    if (a.isCont !== b.isCont) return a.isCont ? -1 : 1;
    return b.h - a.h || b.w - a.w;
  });

  const nodeCount = childBoxes.filter((b) => !b.isCont).length;
  const contCount = childBoxes.length - nodeCount;
  const widest = Math.max(...childBoxes.map((b) => b.w));

  // Ширину полки берём не от одного самого широкого ребёнка, а от общей площади:
  // иначе несколько широких вложенных локаций встают в одну колонку и схема
  // вытягивается в узкую ленту, которую на экране видно только процентов на сорок.
  let targetWidth;
  if (contCount >= 2) {
    const area = childBoxes.reduce((acc, b) => acc + (b.w + GAP) * (b.h + GAP), 0);
    targetWidth = Math.max(widest, Math.sqrt(area) * 1.35);
  } else {
    targetWidth = Math.max(gridCols(nodeCount || childBoxes.length) * (NODE_W + GAP), widest);
  }

  const shelves = packShelves(childBoxes, targetWidth, GAP);

  let y = PAD_TOP;
  let maxW = 0;
  for (const sh of shelves) {
    let x = PAD;
    for (const it of sh.items) {
      it.rx = x;
      it.ry = y + (sh.h - it.h) / 2;
      x += it.w + GAP;
    }
    maxW = Math.max(maxW, x - GAP + PAD);
    y += sh.h + GAP;
  }
  const w = Math.max(260, maxW);
  const h = Math.max(150, y - GAP + PAD);

  // применить относительные позиции детей
  for (const it of childBoxes) {
    const dx = c.x + it.rx, dy = c.y + it.ry;
    if (it.isCont) moveContainer(s, it.ref, dx - it.ref.x, dy - it.ref.y);
    else { it.ref.x = dx; it.ref.y = dy; }
  }

  c.w = w; c.h = h;
  return { w, h };
}

/** Перенос локации вместе со всем содержимым. */
export function moveContainer(s, cont, dx, dy) {
  if (!dx && !dy) return;
  cont.x += dx; cont.y += dy;
  for (const n of s.nodes.values()) if (n.locId === cont.id) { n.x += dx; n.y += dy; }
  for (const cc of s.conts.values()) if (cc.parentId === cont.id) moveContainer(s, cc, dx, dy);
}

/** Локации верхнего уровня раскладываем полками — гарантированно без наложений. */
function placeRoots(s) {
  const roots = [...s.conts.values()].filter((c) => !c.parentId);
  if (!roots.length) return;

  const items = roots.map((c) => ({ ref: c, w: c.w, h: c.h }));
  items.sort((a, b) => b.h - a.h || b.w - a.w);

  const totalArea = items.reduce((acc, it) => acc + it.w * it.h, 0);
  const maxWidth = Math.max(
    Math.sqrt(totalArea) * 1.6,
    ...items.map((i) => i.w),
  );

  const shelves = packShelves(items, maxWidth, GAP * 2);
  let y = 0;
  for (const sh of shelves) {
    let x = 0;
    for (const it of sh.items) {
      moveContainer(s, it.ref, x - it.ref.x, y - it.ref.y);
      x += it.w + GAP * 2;
    }
    y += sh.h + GAP * 2;
  }
}

/** Устройства без локации — отдельной колонкой справа, чтобы не терялись. */
function placeOrphans(s) {
  const orphans = [...s.nodes.values()].filter((n) => !n.locId && !n.hidden);
  if (!orphans.length) return;
  const box = unionRect([...s.conts.values()]) || { x: 0, y: 0, w: 0, h: 0 };
  const cols = gridCols(orphans.length);
  orphans.forEach((n, i) => {
    n.x = box.x + box.w + GAP * 3 + (i % cols) * (NODE_W + GAP);
    n.y = box.y + Math.floor(i / cols) * (NODE_H + GAP);
  });
}

/**
 * Полная автораскладка. Уважает закреплённые элементы: если пользователь
 * что-то расставил руками и закрепил, мы это не трогаем.
 */
export function autoLayout(s, opts = {}) {
  // «Расставить всё заново» — значит и размеры рамок тоже заново. Иначе
  // локация, которую однажды растянули руками, навсегда остаётся растянутой
  // и перекашивает всю схему, а трассам не хватает места для чистого обхода.
  if (opts.force) {
    for (const c of s.conts.values()) c.autoSize = true;
  }
  const roots = [...s.conts.values()].filter((c) => !c.parentId);
  for (const r of roots) sizeAndPlace(s, r.id, new Set());

  placeRoots(s);
  placeOrphans(s);

  // Удержание детей внутри рамки может РАСШИРИТЬ контейнер, а расширенный
  // контейнер наезжает на соседа. Поэтому «вместить → развести» повторяем,
  // пока не перестанет меняться: иначе получаем ровно ту картину, за которую
  // забракована карта v2 — «Серверная» поверх «Офиса».
  for (let pass = 0; pass < 4; pass++) {
    keepInside(s);
    const moved = separateOverlaps(s, null, 12, 'cont');
    if (!moved) break;
  }
  keepInside(s);
  return s;
}

/** Раскладка только внутри одной локации — по кнопке «прибрать здесь». */
export function layoutInside(s, locId) {
  const c = s.conts.get(locId);
  if (!c) return;
  const keepX = c.x, keepY = c.y;
  sizeAndPlace(s, locId, new Set());
  moveContainer(s, c, keepX - c.x, keepY - c.y);
  keepInside(s);
}

/**
 * Удержание детей внутри рамки локации.
 * Если ребёнок вылез — двигаем его обратно; если контейнер меньше содержимого
 * и разрешён автосайз — расширяем контейнер. Так «устройство за границей»
 * становится невозможным состоянием, а не тем, что надо ловить глазами.
 */
export function keepInside(s) {
  for (const id of s.order) {
    const c = s.conts.get(id);
    if (!c) continue;
    const { nodes, conts } = childrenOf(s, id);
    const kids = [...nodes, ...conts];
    if (!kids.length) continue;

    // ШАГ 1. Сдвинуть содержимое внутрь, если оно вылезло сверху или слева.
    // Двигаем ВСЕХ детей одинаково — их взаимное расположение не меняется.
    const u0 = unionRect(kids);
    if (u0) {
      const dx = Math.max(0, c.x + PAD - u0.x);
      const dy = Math.max(0, c.y + PAD_TOP - u0.y);
      if (dx || dy) {
        for (const k of kids) {
          if (k.kind === 'cont') moveContainer(s, k, dx, dy);
          else { k.x += dx; k.y += dy; }
        }
      }
    }

    // ШАГ 2. Растянуть рамку под содержимое. Делаем это ДО подрезки: если
    // сначала подрезать, дети собьются в кучу у края и наложатся друг на друга.
    // Рамку растим всегда, даже когда размер задан руками: содержимое важнее
    // заданной ширины, а сузить обратно пользователь может сам.
    const u1 = unionRect(kids);
    if (u1) {
      const needW = u1.x + u1.w + PAD - c.x;
      const needH = u1.y + u1.h + PAD - c.y;
      if (needW > c.w) c.w = needW;
      if (needH > c.h) c.h = needH;
    }

    // ШАГ 3. Подрезка — только теперь, когда места заведомо хватает.
    // Она нужна лишь как страховка от координат, приехавших извне.
    const box = innerBox(c);
    for (const k of kids) {
      if (box.w < k.w || box.h < k.h) continue;   // не помещается — не сминаем
      const nx = clamp(k.x, box.x, box.x + box.w - k.w);
      const ny = clamp(k.y, box.y, box.y + box.h - k.h);
      if (nx === k.x && ny === k.y) continue;
      if (k.kind === 'cont') moveContainer(s, k, nx - k.x, ny - k.y);
      else { k.x = nx; k.y = ny; }
    }
  }
}

/**
 * Разведение наложений между соседями одного уровня.
 * Используется при ручном перетаскивании: то, что тянет пользователь,
 * стоит на месте, остальные расступаются.
 */
/**
 * Разведение наложений между соседями одного уровня.
 *
 * @param only 'cont' — разводить только локации, 'node' — только устройства,
 *             пусто — всё. После автораскладки устройства уже стоят идеальной
 *             сеткой, и лишний проход разведения её только портит: прямоугольники
 *             с запасом задевают друг друга и разъезжаются без нужды.
 */
export function separateOverlaps(s, fixedId = null, maxIter = 24, only = null) {
  const groups = new Map();
  if (only !== 'node') {
    for (const c of s.conts.values()) {
      const k = c.parentId || 'root';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(c);
    }
  }
  if (only !== 'cont') {
    for (const n of s.nodes.values()) {
      if (n.hidden) continue;
      const k = n.locId ? `n:${n.locId}` : 'n:root';
      if (!groups.has(k)) groups.set(k, []);
      groups.get(k).push(n);
    }
  }

  let anyMoved = false;
  for (const [, list] of groups) {
    if (list.length < 2) continue;
    // Локации крупные, им нужен и зазор побольше, и больше проходов: когда две
    // рамки наложены почти полностью, за один шаг их не растащить.
    const isCont = list[0].kind === 'cont';
    const margin = isCont ? GAP : 6;
    const iters = isCont ? maxIter * 3 : maxIter;

    for (let iter = 0; iter < iters; iter++) {
      let moved = false;
      for (let i = 0; i < list.length; i++) {
        for (let j = i + 1; j < list.length; j++) {
          const A = list[i], B = list[j];
          const a = { x: A.x - margin, y: A.y - margin, w: A.w + margin * 2, h: A.h + margin * 2 };
          const b = { x: B.x - margin, y: B.y - margin, w: B.w + margin * 2, h: B.h + margin * 2 };
          if (!rectsOverlap(a, b)) continue;
          let v = mtv(a, b);
          // Рамки совпали точка в точку — вектор выталкивания вырождается,
          // и пара может «дрожать» на месте. Толкаем по детерминированной оси.
          if (Math.abs(v.x) < 0.5 && Math.abs(v.y) < 0.5) {
            v = { x: (A.w + margin * 2) * (A.id <= B.id ? -1 : 1), y: 0 };
          }
          // Неподвижен ТОЛЬКО тот элемент, который пользователь держит под
          // курсором. Флаг pinned здесь не при чём: он ставится при каждом
          // перетаскивании, и если его учитывать, то после пары ручных правок
          // вообще ничего не разводится — устройства и локации так и остаются
          // друг на друге.
          const aFixed = A.id === fixedId;
          const bFixed = B.id === fixedId;
          if (aFixed && bFixed) continue;
          moved = true; anyMoved = true;
          if (aFixed) shift(s, B, -v.x, -v.y);
          else if (bFixed) shift(s, A, v.x, v.y);
          else { shift(s, A, v.x / 2, v.y / 2); shift(s, B, -v.x / 2, -v.y / 2); }
        }
      }
      if (!moved) break;
    }
  }
  keepInside(s);
  return anyMoved;
}

function shift(s, ent, dx, dy) {
  if (ent.kind === 'cont') moveContainer(s, ent, dx, dy);
  else { ent.x += dx; ent.y += dy; }
}
